import colours from "../../theme/colours";

function readFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const numericValue = readFiniteNumber(value);
    if (numericValue != null) {
      return numericValue;
    }
  }

  return null;
}

function encodeSignedNumber(value) {
  let signedValue = value < 0 ? ~(value << 1) : value << 1;
  let output = "";

  while (signedValue >= 0x20) {
    output += String.fromCharCode((0x20 | (signedValue & 0x1f)) + 63);
    signedValue >>= 5;
  }

  output += String.fromCharCode(signedValue + 63);
  return output;
}

export function normaliseGeoRoutePoint(point, index = 0) {
  const lat = firstFiniteNumber(point?.lat, point?.latitude);
  const lng = firstFiniteNumber(
    point?.lng,
    point?.lon,
    point?.longitude,
    point?.long,
  );

  if (
    lat == null ||
    lng == null ||
    Math.abs(lat) > 90 ||
    Math.abs(lng) > 180
  ) {
    return null;
  }

  return {
    lat,
    lng,
    tSeconds: firstFiniteNumber(
      point?.tSeconds,
      point?.seconds,
      point?.t,
      point?.elapsedSeconds,
      index,
    ),
    totalDistanceMeters: firstFiniteNumber(
      point?.totalDistanceMeters,
      point?.distanceMeters,
      point?.distanceMetres,
    ),
    accuracyMeters: firstFiniteNumber(
      point?.accuracyMeters,
      point?.accuracy,
      point?.accuracyMetres,
    ),
  };
}

export function encodeGooglePolyline(points = []) {
  const normalisedPoints = (Array.isArray(points) ? points : [])
    .map((point, index) => normaliseGeoRoutePoint(point, index))
    .filter(Boolean);

  if (!normalisedPoints.length) {
    return "";
  }

  let previousLat = 0;
  let previousLng = 0;
  let encoded = "";

  normalisedPoints.forEach((point) => {
    const lat = Math.round(point.lat * 1e5);
    const lng = Math.round(point.lng * 1e5);

    encoded += encodeSignedNumber(lat - previousLat);
    encoded += encodeSignedNumber(lng - previousLng);

    previousLat = lat;
    previousLng = lng;
  });

  return encoded;
}

export function buildGoogleStaticMapUrl(
  points = [],
  { apiKey, width = 640, height = 360, scale = 2 } = {},
) {
  const normalisedPoints = (Array.isArray(points) ? points : [])
    .map((point, index) => normaliseGeoRoutePoint(point, index))
    .filter(Boolean);

  if (!apiKey || !normalisedPoints.length) {
    return null;
  }

  const encodedPath = encodeGooglePolyline(normalisedPoints);
  if (!encodedPath) {
    return null;
  }

  const startPoint = normalisedPoints[0];
  const endPoint = normalisedPoints[normalisedPoints.length - 1];
  const params = new URLSearchParams();

  params.set("key", apiKey);
  params.set("size", `${Math.max(1, Math.round(width))}x${Math.max(
    1,
    Math.round(height),
  )}`);
  params.set("scale", String(Math.max(1, Math.round(scale))));
  params.set("maptype", "roadmap");
  params.append(
    "path",
    `color:0x${colours.primary.replace("#", "").toUpperCase()}CC|weight:5|enc:${encodedPath}`,
  );
  params.append(
    "markers",
    `color:0x${colours.secondary.replace("#", "").toUpperCase()}|${startPoint.lat},${startPoint.lng}`,
  );

  if (normalisedPoints.length > 1) {
    params.append(
      "markers",
      `color:0x${colours.primary.replace("#", "").toUpperCase()}|${endPoint.lat},${endPoint.lng}`,
    );
  }

  return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
}
